import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";

/** ---------- Re-trigger on scroll (NO page reload needed) ---------- */
function useInViewRepeat<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false);

  // Track small-screen state and force `inView` on mobile so content is visible without strict thresholds
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(max-width: 768px)");
    const handleMatch = () => {
      setIsSmallScreen(m.matches);
      if (m.matches) setInView(true);
      else setInView(false);
    };
    handleMatch();
    if (m.addEventListener) m.addEventListener("change", handleMatch);
    else m.addListener(handleMatch);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", handleMatch);
      else m.removeListener(handleMatch);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Skip observer on small screens (we already forced visibility) to avoid flicker
    if (isSmallScreen) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        // true when entering, false when leaving -> animation can replay
        setInView(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px", ...(options || {}) }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [isSmallScreen, options?.root, options?.rootMargin, options?.threshold]);

  return { ref, inView, isSmallScreen };
}

/** ---------- Small helpers ---------- */
const easeOut = [0.16, 1, 0.3, 1] as const;

export default function DigisocietyPage() {
  const reduceMotion = useReducedMotion();

  const features = useMemo(
    () => [
      {
        topBar: "from-blue-500 to-purple-500",
        iconWrap: "from-blue-100 to-blue-50 text-blue-600",
        badge: "text-blue-600",
        icon: "📢",
        title: "Smart Notices",
        desc: "Send notices, bills and reminders to the residents in society instantly.",
      },
      {
        topBar: "from-green-500 to-emerald-500",
        iconWrap: "from-green-100 to-green-50 text-green-600",
        badge: "text-green-600",
        icon: "🛂",
        title: "Visitor Management",
        desc: "Approve/disapprove visitors, invite guests, and enable gate security with OTPs.",
      },
      {
        topBar: "from-purple-500 to-pink-500",
        iconWrap: "from-purple-100 to-purple-50 text-purple-600",
        badge: "text-purple-600",
        icon: "💬",
        title: "Community Hub",
        desc: "Ask for help, opinions or join an open forum with other members.",
      },
      {
        topBar: "from-amber-500 to-orange-500",
        iconWrap: "from-amber-100 to-amber-50 text-amber-600",
        badge: "text-amber-600",
        icon: "🧑‍🔧",
        title: "Staff Management",
        desc: "Track attendance with IN/OUT times for all staff members employed by the society.",
      },
      {
        topBar: "from-cyan-500 to-blue-500",
        iconWrap: "from-cyan-100 to-cyan-50 text-cyan-600",
        badge: "text-cyan-600",
        icon: "🧰",
        title: "Home Services",
        desc: "Connect to stores, mechanics, and other professionals in and around your locality.",
      },
      {
        topBar: "from-red-500 to-rose-500",
        iconWrap: "from-red-100 to-red-50 text-red-600",
        badge: "text-red-600",
        icon: "🚑",
        title: "Emergency Response",
        desc: "Quickly reach Police, Fire Brigade, Ambulance or the on-duty guard.",
      },
      {
        topBar: "from-indigo-500 to-blue-500",
        iconWrap: "from-indigo-100 to-indigo-50 text-indigo-600",
        badge: "text-indigo-600",
        icon: "🏢",
        title: "Facility Booking",
        desc: "Request bookings for society amenities right from your smartphone.",
      },
      {
        topBar: "from-emerald-500 to-teal-500",
        iconWrap: "from-emerald-100 to-emerald-50 text-emerald-600",
        badge: "text-emerald-600",
        icon: "🏷️",
        title: "Flat Directory",
        desc: "Contact details of all members & tenants for emergencies or social calls.",
      },
      {
        topBar: "from-fuchsia-500 to-pink-500",
        iconWrap: "from-fuchsia-100 to-fuchsia-50 text-fuchsia-600",
        badge: "text-fuchsia-600",
        icon: "🖼️",
        title: "Event Gallery",
        desc: "Access photos and videos of cultural & social events in the society.",
      },
      {
        topBar: "from-violet-500 to-purple-500",
        iconWrap: "from-violet-100 to-violet-50 text-violet-600",
        badge: "text-violet-600",
        icon: "💳",
        title: "Secure Payments",
        desc: "Pay maintenance and utility bills easily on a secure platform.",
      },
      {
        topBar: "from-rose-500 to-pink-500",
        iconWrap: "from-rose-100 to-rose-50 text-rose-600",
        badge: "text-rose-600",
        icon: "📮",
        title: "Complaint Management",
        desc: "Raise and manage complaints that need society intervention.",
      },
      {
        topBar: "from-sky-500 to-cyan-500",
        iconWrap: "from-sky-100 to-sky-50 text-sky-600",
        badge: "text-sky-600",
        icon: "📊",
        title: "Instant Polls",
        desc: "Conduct polls to collect members' opinions on society matters.",
      },
    ],
    []
  );

  /** ---------- Sections InView ---------- */
  const heroWrap = useInViewRepeat<HTMLDivElement>({ threshold: 0.25 });
  const heroLeft = useInViewRepeat<HTMLDivElement>({ threshold: 0.25 });
  const heroRight = useInViewRepeat<HTMLDivElement>({ threshold: 0.25 });

  const featureHeader = useInViewRepeat<HTMLDivElement>({ threshold: 0.25 });
  const featureGrid = useInViewRepeat<HTMLDivElement>({ threshold: 0.15 });

  // Derived mobile flag across the section (simplifies choosing mobile variants)
  const isMobile = heroWrap.isSmallScreen || heroLeft.isSmallScreen || heroRight.isSmallScreen || featureHeader.isSmallScreen || featureGrid.isSmallScreen;


  /** ---------- Variants (different animations for different elements) ---------- */
  const vFadeUp = {
    hidden: { opacity: 0, y: 26 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeOut } },
  };

  const vSlideRight = {
    hidden: { opacity: 0, x: -28 },
    show: { opacity: 1, x: 0, transition: { duration: 0.85, ease: easeOut } },
  };

  const vSlideLeft = {
    hidden: { opacity: 0, x: 28 },
    show: { opacity: 1, x: 0, transition: { duration: 0.85, ease: easeOut } },
  };

  const vScaleIn = {
    hidden: { opacity: 0, scale: 0.92 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: easeOut } },
  };

  const vStagger = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.07,
        delayChildren: 0.1,
      },
    },
  };

  const vCard = (i: number) => ({
    hidden: { opacity: 0, y: 18, scale: 0.98 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.65,
        ease: easeOut,
        delay: reduceMotion ? 0 : i * 0.03,
      },
    },
  });

  const vGlowFloat = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: easeOut } },
  };

  // Mobile-friendly variants (lighter, faster) so transitions also run on small screens
  const vFadeUpMobile = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easeOut } },
  };

  const vSlideLeftMobile = {
    hidden: { opacity: 0, x: 8 },
    show: { opacity: 1, x: 0, transition: { duration: 0.45, ease: easeOut } },
  };

  const vSlideRightMobile = {
    hidden: { opacity: 0, x: -8 },
    show: { opacity: 1, x: 0, transition: { duration: 0.45, ease: easeOut } },
  };

  const vScaleInMobile = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.45, ease: easeOut } },
  };

  const vStaggerMobile = { hidden: {}, show: {} };

  const vCardMobile = (i: number) => ({
    hidden: { opacity: 0, y: 8, scale: 1 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: easeOut } },
  });

  const vGlowFloatMobile = {
    hidden: { opacity: 0, y: 6 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easeOut } },
  };

  // Helper: pick desktop or mobile variant
  const chooseVariant = (desktop: any, mobile: any) => (isMobile ? mobile : desktop);

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <Navigation />
<section
  className="relative min-h-screen flex items-center overflow-hidden bg-[#040814] text-white pt-12 md:pt-28 lg:pt-32 pb-8"
  aria-label="About Digi-Society"
>
  {/* Background Elements (always running, subtle) */}
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <motion.div
      className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-blue-900/20 to-purple-900/10 rounded-full blur-3xl"
      animate={reduceMotion || heroWrap.isSmallScreen ? {} : { y: [0, -14, 0], x: [0, 10, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute bottom-1/4 -right-20 w-96 h-96 bg-gradient-to-l from-indigo-900/20 to-blue-900/10 rounded-full blur-3xl"
      animate={reduceMotion || heroWrap.isSmallScreen ? {} : { y: [0, 18, 0], x: [0, -10, 0] }}
      transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-64 bg-gradient-to-t from-transparent via-blue-900/5 to-transparent" />
    <motion.div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(800px circle at 20% 20%, rgba(59,130,246,0.12), transparent 40%), radial-gradient(700px circle at 85% 55%, rgba(168,85,247,0.10), transparent 45%)",
      }}
      animate={reduceMotion || heroWrap.isSmallScreen ? {} : { opacity: [0.7, 1, 0.7] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>

  <div ref={heroWrap.ref} className="relative z-10 w-full">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* LEFT */}
        <div ref={heroLeft.ref} className="space-y-6 lg:space-y-8">
          {/* heading - separate animation */}
          <motion.h1
            variants={chooseVariant(vFadeUp, vFadeUpMobile)}
            initial="hidden"
            animate={heroLeft.inView || heroLeft.isSmallScreen ? "show" : "hidden"}
            className="pt-10 sm:pt-0 text-4xl sm:text-5xl lg:text-5xl font-bold tracking-tight"
          >
            Revolutionizing
            <span className="block mt-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent pb-4">
              Community Living
            </span>
          </motion.h1>
          {/* paragraph - different animation */}
          <motion.p
            variants={chooseVariant(vGlowFloat, vGlowFloatMobile)}
            initial="hidden"
            animate={heroLeft.inView || heroLeft.isSmallScreen ? "show" : "hidden"}
            className="text-md text-gray-300 leading-relaxed"
          >
            DIGI-SOCIETY is an advanced mobile application transforming how residents in housing societies,
            apartments, and gated communities interact with their environment. We&apos;re replacing outdated
            paper-based systems with a seamless digital ecosystem that enhances security, efficiency, and community engagement.
          </motion.p>

          {/* badges - staggered */}
          <motion.div
            variants={chooseVariant(vStagger, vStaggerMobile)}
            initial="hidden"
            animate={heroLeft.inView || heroLeft.isSmallScreen ? "show" : "hidden"}
            className="grid grid-cols-2 gap-4 pt-4"
          >
            <motion.div variants={vScaleIn} className="flex items-center gap-3">
              <motion.div
                className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center"
                whileHover={reduceMotion ? {} : { scale: 1.08, rotate: -2 }}
                transition={{ duration: 0.25 }}
              >
                <span className="text-blue-400">✓</span>
              </motion.div>
              <span className="text-sm font-medium">100% Digital</span>
            </motion.div>

            <motion.div variants={vScaleIn} className="flex items-center gap-3">
              <motion.div
                className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center"
                whileHover={reduceMotion ? {} : { scale: 1.08, rotate: 2 }}
                transition={{ duration: 0.25 }}
              >
                <span className="text-purple-400">🛡️</span>
              </motion.div>
              <span className="text-sm font-medium">Enhanced Security</span>
            </motion.div>
          </motion.div>
        </div>

        {/* RIGHT IMAGE */}
        <div ref={heroRight.ref} className="relative lg:justify-self-end">
          <motion.div
            variants={chooseVariant(vSlideLeft, vSlideLeftMobile)}
            initial="hidden"
            animate={heroRight.inView || heroRight.isSmallScreen ? "show" : "hidden"}
            className="relative rounded-3xl overflow-hidden shadow-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
            <div className="relative p-2 bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/50 rounded-3xl">
              <motion.img
                src="/about-img.png"
                alt="Digi-Society Interface"
                className="rounded-2xl w-full object-cover"
                whileHover={reduceMotion || heroRight.isSmallScreen ? {} : { scale: 1.04 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />

              {/* floating blobs (different animation) */}
              <motion.div
                className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl -z-10 opacity-30 blur-xl"
                animate={reduceMotion || heroRight.isSmallScreen ? {} : { rotate: [0, 8, 0], scale: [1, 1.06, 1] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full -z-10 opacity-20 blur-xl"
                animate={reduceMotion || heroRight.isSmallScreen ? {} : { y: [0, -10, 0], x: [0, 10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </div>
</section>

{/* ===================== FEATURES (LIGHT) ===================== */}
      <section
        id="features"
        className="bg-gradient-to-b from-white to-gray-50 py-24"
        aria-label="Features"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div ref={featureHeader.ref} className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              variants={chooseVariant(vScaleIn, vScaleInMobile)}
              initial="hidden"
              animate={featureHeader.inView || featureHeader.isSmallScreen ? "show" : "hidden"}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 mb-4"
            >
              <span className="text-blue-600 text-sm font-semibold">POWERFUL FEATURES</span>
            </motion.div>

            <motion.h2
              variants={chooseVariant(vFadeUp, vFadeUpMobile)}
              initial="hidden"
              animate={featureHeader.inView || featureHeader.isSmallScreen ? "show" : "hidden"}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900"
            >
              Everything You Need For A
              <span className="block mt-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Modern Society
              </span>
            </motion.h2>

            <motion.p
              variants={chooseVariant(vGlowFloat, vGlowFloatMobile)}
              initial="hidden"
              animate={featureHeader.inView || featureHeader.isSmallScreen ? "show" : "hidden"}
              className="mt-4 text-lg text-gray-600"
            >
              Comprehensive tools designed to streamline society management and enhance resident experience
            </motion.p>
          </div>

          {/* Grid */}
          <div ref={featureGrid.ref}>
            <motion.div
              variants={chooseVariant(vStagger, vStaggerMobile)}
              initial="hidden"
              animate={featureGrid.inView || featureGrid.isSmallScreen ? "show" : "hidden"}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {features.map((f, i) => (
                <motion.article
                  key={f.title}
                  variants={vCard(i)}
                  whileHover={reduceMotion ? {} : { y: -6 }}
                  transition={{ duration: 0.25 }}
                  className="group"
                >
                  <div className="relative h-full rounded-2xl bg-white p-6 border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-300">
                    {/* top gradient bar */}
                    <motion.div
                      className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${f.topBar} rounded-t-2xl`}
                      initial={{ scaleX: 0.4, opacity: 0.5 }}
                      animate={
                        featureGrid.inView || featureGrid.isSmallScreen
                          ? { scaleX: 1, opacity: 1, transition: { duration: 0.6, ease: easeOut } }
                          : { scaleX: 0.4, opacity: 0.5 }
                      }
                      style={{ transformOrigin: "left" }}
                    />

                    {/* icon */}
                    <motion.div
                      className={`h-14 w-14 rounded-xl bg-gradient-to-br ${f.iconWrap} grid place-items-center text-2xl mb-4`}
                      whileHover={reduceMotion || isMobile ? {} : { rotate: -3, scale: 1.08 }}
                      transition={{ duration: 0.25 }}
                    >
                      {f.icon}
                    </motion.div>

                    {/* title */}
                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      animate={
                        featureGrid.inView || featureGrid.isSmallScreen
                          ? { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.05 } }
                          : { opacity: 0, y: 10 }
                      }
                      className="text-lg font-bold text-gray-900"
                    >
                      {f.title}
                    </motion.h3>

                    {/* desc */}
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={
                        featureGrid.inView || featureGrid.isSmallScreen
                          ? { opacity: 1, transition: { duration: 0.6, delay: 0.1 } }
                          : { opacity: 0 }
                      }
                      className="mt-2 text-sm text-gray-600"
                    >
                      {f.desc}
                    </motion.p>

                    {/* hover glow overlay */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl pointer-events-none"
                      initial={{ opacity: 0 }}
                      whileHover={reduceMotion || isMobile ? {} : { opacity: 1 }}
                      transition={{ duration: 0.25 }}
                      style={{
                        background:
                          "radial-gradient(600px circle at 40% 0%, rgba(59,130,246,0.10), transparent 45%), radial-gradient(500px circle at 80% 60%, rgba(168,85,247,0.08), transparent 55%)",
                      }}
                    />
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
